import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { DistrictScope } from 'district-ui-client/utils/student-events/stats-scope'

export interface SummaryDistrictRexResponse {
  summary: {
    schools: {
      school_id: number
      lessons_completed: number
      total_time: number
      total_usage: number
      books_read: number
      spelling_completed: number
      assessments: number
      stadium_events: number
      lexile_growth: Nullable<number>
      books_read_0_300: Nullable<number>
      books_read_300_500: Nullable<number>
      books_read_500_800: Nullable<number>
      books_read_800_2000: Nullable<number>
      books_read_fiction: Nullable<number>
      books_read_nonfiction: Nullable<number>
      average_books_read_per_student: Nullable<number>
      average_spelling_completed_per_student: Nullable<number>
      average_lessons_completed_per_student: Nullable<number>
      average_assessments_per_student: Nullable<number>
      average_initial_lesson_lexile_per_student: Nullable<number>
      average_current_lesson_lexile_per_student: Nullable<number>
    }[]
    district: {
      lessons_completed: number
      total_time: number
      lexile_growth: Nullable<number>
      books_read: number
      spelling_completed: number
      total_usage: number
      assessments: number
      stadium_events: number
      books_read_0_300: Nullable<number>
      books_read_300_500: Nullable<number>
      books_read_500_800: Nullable<number>
      books_read_800_2000: Nullable<number>
      books_read_fiction: Nullable<number>
      books_read_nonfiction: Nullable<number>
      average_lessons_completed_per_student: Nullable<number>
      average_assessments_per_student: Nullable<number>
      average_spelling_completed_per_student: Nullable<number>
      average_books_read_per_student: Nullable<number>
      average_initial_lesson_lexile_per_student: Nullable<number>
      average_current_lesson_lexile_per_student: Nullable<number>
    }
  }
}

export type SummaryDistrictRexData = SummaryDistrictRexResponse['summary']

export async function summaryRex(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: DistrictScope,
  period: NormalizedInterval,
): Promise<Nullable<SummaryDistrictRexData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummaryDistrictRexResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.schools.length === 0) return null

  return summary
}

export interface SummaryDistrictMsResponse {
  summary: {
    schools: {
      total_time: number
      school_id: number
      lessons_completed: number
      assessments: number
      driving_tests_completed: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      average_lessons_completed_per_student: Nullable<number>
      total_usage: number
      average_assessments_per_student: Nullable<number>
      average_driving_tests_completed_per_student: Nullable<number>
    }[]
    district: {
      total_time: number
      lessons_completed: number
      assessments: number
      driving_tests_completed: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      average_lessons_completed_per_student: Nullable<number>
      total_usage: number
      average_assessments_per_student: Nullable<number>
      average_driving_tests_completed_per_student: Nullable<number>
    }
  }
}

export type SummaryDistrictMsData = SummaryDistrictMsResponse['summary']

export async function summaryMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: DistrictScope,
  period: NormalizedInterval,
): Promise<Nullable<SummaryDistrictMsData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummaryDistrictMsResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.schools.length === 0) return null

  return summary
}

export interface SummaryDistrictReResponse {
  summary: {
    schools: {
      school_id: number
      lessons_completed: number
      total_time: number
      total_usage: number
      books_read: number
      spelling_completed: number
      assessments: number
      stories_completed: number
      driving_tests_completed: number
      lexile_growth: Nullable<number>
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      average_lessons_completed_per_student: Nullable<number>
      average_books_read_per_student: Nullable<number>
      average_assessments_per_student: Nullable<number>
      average_spelling_completed_per_student: Nullable<number>
      average_stories_completed_per_student: Nullable<number>
      average_driving_tests_completed_per_student: Nullable<number>
      average_initial_lesson_lexile_per_student: Nullable<number>
      average_current_lesson_lexile_per_student: Nullable<number>
    }[]
    district: {
      lessons_completed: number
      total_time: number
      lexile_growth: Nullable<number>
      books_read: number
      spelling_completed: number
      total_usage: number
      assessments: number
      stories_completed: number
      driving_tests_completed: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      average_lessons_completed_per_student: Nullable<number>
      average_assessments_per_student: Nullable<number>
      average_driving_tests_completed_per_student: Nullable<number>
      average_spelling_completed_per_student: Nullable<number>
      average_stories_completed_per_student: Nullable<number>
      average_books_read_per_student: Nullable<number>
      average_initial_lesson_lexile_per_student: Nullable<number>
      average_current_lesson_lexile_per_student: Nullable<number>
    }
  }
}

export type SummaryDistrictReData = SummaryDistrictReResponse['summary']

export async function summaryRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: DistrictScope,
  period: NormalizedInterval,
): Promise<Nullable<SummaryDistrictReData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummaryDistrictReResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.schools.length === 0) return null

  return summary
}
